import React, { useState, useEffect } from 'react';
import { Plus, FileText, Eye, Edit, Trash2, CheckCircle, XCircle, Clock, X, ShoppingCart } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import RequisitionForm from './RequisitionForm';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/currency';
import { useOrganization } from '../../hooks/useOrganization';
import { formatDate } from '../../utils/dateFormat';

const PurchaseRequisition = () => {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRequisition, setEditingRequisition] = useState(null);
  const [viewingRequisition, setViewingRequisition] = useState(null);
  const [showCreatePO, setShowCreatePO] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [vendors, setVendors] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    fetchRequisitions();
    fetchVendors();
  }, [statusFilter]);

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/vendors');
      setVendors(response.data.vendors || response.data || []);
    } catch (error) {
      toast.error('Failed to fetch vendors');
    }
  };

  const fetchRequisitions = async () => {
    try {
      const params = statusFilter !== 'ALL' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`/api/requisitions${params}`);
      setRequisitions(response.data.requisitions || response.data || []);
    } catch (error) {
      toast.error('Failed to fetch requisitions');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingRequisition(null);
    fetchRequisitions();
  };

  const handleEdit = (requisition) => {
    setEditingRequisition(requisition);
    setShowForm(true);
  };

  const handleView = (requisition) => {
    setViewingRequisition(requisition);
  };

  const handleApprove = async (requisition) => {
    try {
      await axios.put(`/api/requisitions/${requisition.id}/status`, {
        status: 'APPROVED',
        approvedBy: user.id
      });
      toast.success('Requisition approved successfully');
      fetchRequisitions();
    } catch (error) {
      toast.error('Failed to approve requisition');
    }
  };

  const handleReject = async (requisition) => {
    if (!window.confirm('Are you sure you want to reject this requisition?')) return;
    
    try {
      await axios.put(`/api/requisitions/${requisition.id}/status`, {
        status: 'REJECTED',
        approvedBy: user.id
      });
      toast.success('Requisition rejected');
      fetchRequisitions();
    } catch (error) {
      toast.error('Failed to reject requisition');
    }
  };

  const handleDelete = async (requisition) => {
    if (!window.confirm(`Are you sure you want to delete requisition ${requisition.requestNo}?`)) return;
    
    try {
      await axios.delete(`/api/requisitions/${requisition.id}`);
      toast.success('Requisition deleted successfully');
      fetchRequisitions();
    } catch (error) {
      toast.error('Failed to delete requisition');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'FULFILLED': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-100 text-red-800';
      case 'HIGH': return 'bg-orange-100 text-orange-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const canApprove = (requisition) => {
    return (user?.role === 'ADMIN' || user?.role === 'MANAGER') && 
           requisition.status === 'PENDING' && 
           requisition.requesterId !== user.id;
  };

  const canCreatePO = (requisition) => {
    return (user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'STOREKEEPER') && 
           requisition.status === 'APPROVED';
  };

  const handleCreatePO = (requisition) => {
    setSelectedRequisition(requisition);
    setShowCreatePO(true);
  };

  const handleCreatePOSubmit = async (vendorId) => {
    try {
      await axios.post(`/api/purchase-orders/from-requisition/${selectedRequisition.id}`, {
        vendorId: parseInt(vendorId)
      });
      toast.success('Purchase Order created successfully');
      setShowCreatePO(false);
      setSelectedRequisition(null);
      fetchRequisitions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create purchase order');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Purchase Requisitions</h2>
          <p className="text-sm text-gray-600 mt-1">
            Workflow: Employee creates → Manager approves → Procurement creates PO
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="FULFILLED">Fulfilled</option>
          </select>
          <button 
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Requisition
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requester</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-4 text-center">Loading...</td>
                </tr>
              ) : requisitions.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                    No requisitions found
                  </td>
                </tr>
              ) : (
                requisitions.map((requisition) => (
                  <tr key={requisition.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-blue-600">
                      {requisition.requestNo}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {requisition.requester?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(requisition.priority)}`}>
                        {requisition.priority || 'MEDIUM'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {requisition.items?.length || 0} items
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatCurrency(requisition.totalAmount || 0, organization?.currency_code)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(requisition.status)}`}>
                        {requisition.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {requisition.poNumber ? (
                        <span className="text-blue-600 font-medium">{requisition.poNumber}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatDate(requisition.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(requisition)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Details"
                        >
                          👁️
                        </button>
                        {requisition.status === 'PENDING' && requisition.requesterId === user.id && (
                          <button
                            onClick={() => handleEdit(requisition)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Edit Requisition"
                          >
                            ✏️
                          </button>
                        )}
                        {canApprove(requisition) && (
                          <>
                            <button
                              onClick={() => handleApprove(requisition)}
                              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 font-medium"
                              title="Approve Requisition"
                            >
                              ✓ APPROVE
                            </button>
                            <button
                              onClick={() => handleReject(requisition)}
                              className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 font-medium ml-1"
                              title="Reject Requisition"
                            >
                              ✗ REJECT
                            </button>
                          </>
                        )}
                        {canCreatePO(requisition) && (
                          <button
                            onClick={() => handleCreatePO(requisition)}
                            className="text-purple-600 hover:text-purple-800"
                            title="Create Purchase Order"
                          >
                            🛒
                          </button>
                        )}
                        {(user?.role === 'ADMIN' || requisition.requesterId === user.id) && (
                          <button
                            onClick={() => handleDelete(requisition)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Requisition Form Modal */}
      {showForm && (
        <RequisitionForm
          onClose={() => {
            setShowForm(false);
            setEditingRequisition(null);
          }}
          onSuccess={handleFormSuccess}
          editRequisition={editingRequisition}
        />
      )}

      {/* View Requisition Modal */}
      {viewingRequisition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">Requisition Details</h2>
              <button 
                onClick={() => setViewingRequisition(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Request Number</label>
                  <p className="text-sm text-gray-900">{viewingRequisition.requestNo}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Requester</label>
                  <p className="text-sm text-gray-900">{viewingRequisition.requester?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Priority</label>
                  <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(viewingRequisition.priority)}`}>
                    {viewingRequisition.priority || 'MEDIUM'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(viewingRequisition.status)}`}>
                    {viewingRequisition.status}
                  </span>
                </div>
                <div className="col-span-2">
                  <div className="bg-gray-50 p-3 rounded space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(viewingRequisition.subtotal || 0, organization?.currency_code)}</span>
                    </div>
                    {viewingRequisition.vatRate > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>VAT ({viewingRequisition.vatRate}%):</span>
                        <span>{formatCurrency(viewingRequisition.vatAmount || 0, organization?.currency_code)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold pt-2 border-t">
                      <span>Total Amount:</span>
                      <span>{formatCurrency(viewingRequisition.totalAmount || 0, organization?.currency_code)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created Date</label>
                  <p className="text-sm text-gray-900">{formatDate(viewingRequisition.createdAt)}</p>
                </div>
                {viewingRequisition.poNumber && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Purchase Order</label>
                    <p className="text-sm text-blue-600 font-medium">{viewingRequisition.poNumber}</p>
                  </div>
                )}
              </div>
              
              {viewingRequisition.items && viewingRequisition.items.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Requested Items</label>
                  <div className="border rounded-md overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Est. Price</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {viewingRequisition.items.map((item, index) => {
                          const price = item.estimatedPrice || item.unitPrice || item.price || 0;
                          return (
                            <tr key={index}>
                              <td className="px-4 py-2 text-sm text-gray-900">{item.partName || item.part?.name || `Part ID: ${item.partId}`}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{item.quantity}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(price, organization?.currency_code)}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.quantity * price, organization?.currency_code)}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{item.notes || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {viewingRequisition.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <p className="text-sm text-gray-900">{viewingRequisition.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create PO Modal */}
      {showCreatePO && selectedRequisition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">Create Purchase Order</h2>
              <button 
                onClick={() => {
                  setShowCreatePO(false);
                  setSelectedRequisition(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <h3 className="text-sm font-medium text-blue-900 mb-2">Workflow: Employee → Manager → Procurement</h3>
                <p className="text-sm text-gray-600 mb-1">
                  Requisition: <strong>{selectedRequisition.requestNo}</strong>
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  Requester: <strong>{selectedRequisition.requester?.name}</strong>
                </p>
                <p className="text-sm text-gray-600">
                  Total Amount: <strong>{formatCurrency(selectedRequisition.totalAmount || 0, organization?.currency_code)}</strong>
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Vendor *</label>
                <select 
                  value={selectedVendorId}
                  onChange={(e) => setSelectedVendorId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose a vendor...</option>
                  {vendors.map(vendor => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreatePO(false);
                    setSelectedRequisition(null);
                  }}
                  className="px-4 py-2 border rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!selectedVendorId) {
                      toast.error('Please select a vendor');
                      return;
                    }
                    handleCreatePOSubmit(selectedVendorId);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create Purchase Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseRequisition;